import { useInfiniteQuery } from "react-query";
import type { UseInfiniteQueryOptions } from "react-query";

import { useApiClient } from "api/useApiClient";

import type { ConversationMessagesResponse, GetConversationMessagesArgs } from "../schemas/messaging";

const queryKeyPrefix = "getConversationMessages";
type QueryKey = [typeof queryKeyPrefix, number, number | undefined, number | undefined];
export const getConversationMessagesQueryKey = (
  args: Omit<GetConversationMessagesArgs, "after" | "limit">
): QueryKey => [queryKeyPrefix, args.userID, args.patientID, args.before];

export const useGetConversationMessages = (
  args: GetConversationMessagesArgs,
  options?: Omit<
    UseInfiniteQueryOptions<
      ConversationMessagesResponse,
      Error,
      ConversationMessagesResponse,
      ConversationMessagesResponse,
      QueryKey
    >,
    "queryKey" | "queryFn" | "getNextPageParam"
  >
) => {
  const client = useApiClient();
  const limit = args.limit ?? 15;

  return useInfiniteQuery({
    queryKey: getConversationMessagesQueryKey(args),
    queryFn: ({ pageParam }) =>
      client.getConversationMessages({
        userID: args.userID,
        patientID: args.patientID,
        limit,
        before: pageParam,
      }),
    getNextPageParam: (lastPage: ConversationMessagesResponse) => {
      // If we got fewer messages than the limit, we've reached the end
      if (lastPage.length < limit) {
        return undefined;
      }
      // Return the ID of the oldest message (last in the array) to use as the 'before' cursor
      const oldestMessage = lastPage[lastPage.length - 1];
      return oldestMessage?.id;
    },
    ...options,
  });
};
